import React, { PropsWithChildren } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import Typography from "../typography/Typography";
import { Interpolation } from "@emotion/serialize/dist/emotion-serialize.cjs";
import DragHandleIcon from "../../icons/move.svg?react";
import CloseIcon from "../../icons/cross.svg?react";

export interface CardProps extends PropsWithChildren {
  title?: string;
  subtitle?: string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  gap?: number;
  extraCss?: Interpolation<Theme>;
  stretch?: boolean;
  compact?: boolean;
  noPad?: boolean;
  showDragHandle?: boolean;
  showDashboardControls?: boolean;
  onClose?: () => void;
  blockHeader?: boolean;
  showTitleBlock?: boolean;
}

const Card: React.FC<CardProps> = (props) => {
  const theme = useTheme();
  const cardStyle = css({
    backgroundColor: theme.baseColours.white,
    borderRadius: 12,
    outline: `1px solid ${theme.colours.grey[200]}`,
    boxShadow: theme.shadows.sm,
    display: "flex",
    flexDirection: "column",
    gap: props.blockHeader ? 0 : props.gap ?? 20,
    padding: props.blockHeader ? 0 : props.noPad ? 0 : props.compact ? 12 : 24,
    width: props.fullWidth ? "100%" : "fit-content",
    flex: props.stretch ? 1 : undefined,
    height: props.fullHeight ? "100%" : "fit-content",
    boxSizing: "border-box",
    overflow: props.blockHeader ? "hidden" : "visible",
  });
  const showTitleBlock = props.showTitleBlock !== false && !props.blockHeader;
  const titleBlock =
    showTitleBlock && (props.title || props.subtitle) ? (
      <div>
        {props.title && (
          <Typography
            text={props.title}
            size="lg"
            variant="text"
            weight="semibold"
          />
        )}
        {props.subtitle && (
          <Typography
            text={props.subtitle}
            size="sm"
            variant="text"
            weight="regular"
            color={theme.colours.grey[600]}
          />
        )}
      </div>
    ) : null;

  const blockHeaderTitle =
    props.blockHeader && props.title ? (
      <div
        css={{
          padding: "0.75rem 1rem",
          borderBottom: `1px solid ${theme.colours.grey[200]}`,
          backgroundColor: theme.colours.grey[50],
        }}
      >
        <Typography text={props.title} size="sm" weight="semibold" />
      </div>
    ) : null;

  const contentStyle = props.blockHeader
    ? {
        padding: props.noPad ? 0 : props.compact ? 12 : 16,
        display: "flex",
        flexDirection: "column" as const,
        gap: props.gap ?? 20,
        flex: 1,
      }
    : {};

  return (
    <div css={[cardStyle, props.extraCss]}>
      {props.showDashboardControls && (
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "5px",
          }}
        >
          <div
            className="drag-handle"
            css={{
              cursor: "move",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <DragHandleIcon
              width={10}
              height={10}
              color={theme.colours.grey[400]}
            />
          </div>
          <CloseIcon
            width={16}
            height={16}
            color={theme.colours.grey[400]}
            onClick={props.onClose}
            css={{ cursor: "pointer" }}
          />
        </div>
      )}
      {blockHeaderTitle}
      {titleBlock}
      <div css={props.blockHeader ? contentStyle : undefined}>
        {props.children}
      </div>
    </div>
  );
};

export default Card;
